/**
 * One-place re-brand. A brand supplies a single primary hex; this derives the
 * hover/active shades and returns `--colour-brand-*` overrides for the document
 * root, layered over the theme defaults in themes.css.
 */
export interface BrandColours {
	'--colour-brand-primary': string;
	'--colour-brand-primary-hover': string;
	'--colour-brand-primary-active': string;
}

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function createBrandColours(primaryHex: string): BrandColours {
	if (!HEX_COLOUR.test(primaryHex)) {
		throw new Error(`createBrandColours: expected a 3- or 6-digit hex colour, got "${primaryHex}"`);
	}
	return {
		'--colour-brand-primary': primaryHex,
		'--colour-brand-primary-hover': shade(primaryHex, 0.12),
		'--colour-brand-primary-active': shade(primaryHex, 0.24),
	};
}

/** Darken a hex colour by `amount` (0–1). */
function shade(hex: string, amount: number): string {
	const { r, g, b } = hexToRgb(hex);
	const factor = 1 - amount;
	const clamp = (c: number) => Math.max(0, Math.min(255, Math.round(c * factor)));
	return rgbToHex(clamp(r), clamp(g), clamp(b));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
	const normalised = hex.replace('#', '');
	const full =
		normalised.length === 3
			? normalised
					.split('')
					.map((c) => c + c)
					.join('')
			: normalised;
	return {
		r: parseInt(full.slice(0, 2), 16),
		g: parseInt(full.slice(2, 4), 16),
		b: parseInt(full.slice(4, 6), 16),
	};
}

function rgbToHex(r: number, g: number, b: number): string {
	const toHex = (c: number) => c.toString(16).padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
